<template>
    <div class="flex border-t border-b -mb-px border-gray-200 bg-white">
        <div class="w-16 flex">
            <div
                class="whitespace-pre-line w-full text-sm m-0 px-2 py-1 text-gray-900 border-r border-gray-200 shadow-none relative focus:z-10"
                v-text="reference"
            ></div>
        </div>
        <div class="flex flex-1 flex-col">
            <div class="flex flex-row flex-1 border-b border-gray-200 bg-white">
                <div class="flex flex-1">
                    <!-- Description. -->
                    <div class="flex-1 relative flex">
                        <div
                            class="whitespace-pre-line text-sm w-full -ml-px -my-px px-2 py-1.5 border-r border-gray-200 shadow-none relative resize-none "
                            ref="descLine"
                            :class="!opened?'max-h-[32px] overflow-hidden leading-8 pt-0':''"
                            v-text="description"
                        ></div>
                        <div class="absolute right-2 top-0 z-20">
                            <div
                                class="inline-block text-xs text-gray-600 m-0 align-top pt-2.5 opacity-25 hover:opacity-100"
                                @click="showFS(item.filesystemPath)">
                                <i class="fa fa-upload"></i>
                            </div>
                            <div
                                class="inline-block text-xs text-gray-600 m-0 align-top pt-2.5 opacity-25 hover:opacity-100"
                                @click="toggleOpened">
                                <i class="fa fa-eye" :class="!canBeOpened?'opacity-0':''"></i>
                            </div>
                        </div>
                    </div>
                    <!-- Quantity. -->
                    <div class="w-28 -ml-px flex">
                        <div
                            class="whitespace-pre-line text-sm w-full -ml-px -my-px px-2 py-1.5 shadow-none relative resize-none"
                            v-text="quantity"
                        ></div>
                    </div>
                </div>
            </div>
            <div class="w-full relative flex flex-1">
                <div class="w-full my-1">
                    <progress-report-progress-line :percent="item.complete"
                                                   :min="itemMinPercent"
                                                   :max="itemMaxPercent"
                                                   minColor='bg-green-400'
                                                   :sliderColor='(item.components.length > 0)?"bg-blue-400":"bg-green-400"'
                                                   @change="update"
                    ></progress-report-progress-line>
                </div>
            </div>
            <div class="relative flex-1 px-2 border-t border-r border-gray-200 bg-gray-50 mb-px"
                 v-if="item.components.length > 0">
                <div class="text-xs font-semibold mb-1 mt-1">Components:</div>
                <div class="relative mb-1 flex" v-for="(component,index) in item.components">
                    <div class="w-8 ml-0 text-gray-500 text-sm">
                        <div class="cursor-pointer relative w-[22px] h-[22px] mt-0">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22px"
                                height="22px"
                                viewBox="0 0 36 36"
                                fill="#FFFFFF"
                                stroke="rgb(59 130 246)"
                                stroke-width="1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="absolute"
                                style="left: 0px; top: 0px"
                            >
                                <circle cx="18" cy="18" r="17"/>
                            </svg>
                            <div class="flex justify-center text-blue-500 relative mt-px">
                                <div class="mt-0.5 font-bold leading-4 pt-px" style="font-size: 0.6em"
                                     v-text="component.name.substring(0,3)"></div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow">
                        <div
                            class="w-full text-xs text-gray-600 -ml-px -my-px px-1.5 py-1 border border-gray-200"
                        ><span v-text="component.name"></span><span class="text-xs ml-1 text-gray-400"
                                                                    v-text="'('+math.round(component.ratio*100,2)+'%)'"></span>
                        </div>
                    </div>
                    <div class="w-5/12 my-0.5">
                        <progress-report-progress-line :percent="component.complete"
                                                       :readonly="readonly"
                                                       sliderColor="bg-green-400"
                                                       min-color="bg-green-400"
                                                       @change="(value) => updatedComponent(component,value)">
                        </progress-report-progress-line>
                    </div>
                    <div class="w-16">
                        <page-cell
                            :read-only="readonly"
                            class="w-full text-right text-xs -ml-px -my-px px-2 py-1 mb-0 border border-gray-200"
                            :class="hasComponentChanged(component)?'text-blue-500 font-semibold':'text-gray-500'"
                            placeholder="%"
                            :value="component.complete"
                            @change="(value) => updatedComponent(component,value)"
                        ></page-cell>
                    </div>
                    <div class="w-4 relative">
                        <div class="absolute right-0 top-0 z-20">
                            <div
                                class="inline-block text-xs text-gray-600 m-0 align-top pt-1.5 opacity-25 hover:opacity-100"
                                @click="showFS(component.filesystemPath)">
                                <i class="fa fa-upload"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-20 -ml-px my-0 border-l border-r border-gray-200">
            <page-cell
                :read-only="readonly"
                class="text-sm text-right text-blue-500 m-0 px-1.5 py-1.5 border-none bg-white"
                :class="hasItemChanged()?'text-blue-500 font-semibold':'text-gray-500'"
                placeholder="%"
                v-model="item.complete"
                @change="update"
            ></page-cell>
        </div>
    </div>
</template>

<script>

import {toCurrency, resolveNumericText} from "../../utils";
import ContractedWorksProgressLine from "../commercial-management/contracted-works/ContractedWorksProgressLine.vue";
import ProgressReportProgressLine from "./ProgressReportProgressLine.vue";
import UiDropdown from "../ui/UiDropdown.vue";
import PageCell from "../ui/PageCell.vue";
import * as math from "mathjs";

export default {
    components: {PageCell, UiDropdown, ProgressReportProgressLine, ContractedWorksProgressLine},
    props: {
        progress: Object,
        section: Object,
        task: Object,
        item: Object,
        readonly: Boolean,
        fsModal: Object,
    },
    data() {
        return {
            reference: this.item.reference,
            description: this.item.description,
            quantity: this.item.quantity,
            itemMinPercent: 0,
            itemMaxPercent: 100,
            math,
            opened: false,
            canBeOpened: false,
            originalItemComplete: this.item.complete,
            originalComponentComplete: this.item.components.map((c) => {
                return {id: c.id, complete: c.complete};
            })
        }
    },
    computed: {},
    methods: {
        updatedComponent(component, value) {
            if (!this.readonly) {
                let v = math.min(100, math.max(0, parseFloat(value)));
                let diff = (((v / 100) * component.ratio) * 100) - (((parseFloat(component.complete) / 100) * component.ratio) * 100)
                component.complete = math.round(v, 2) + '%';
                this.item.complete = math.round(parseFloat(this.item.complete) + diff, 2) + '%';
                this.recalcMinMax()
                this.$forceUpdate()
                this.$emit('changed');
            }
        },
        update(value) {
            if (!this.readonly) {
                let v = math.min(100, math.max(0, parseFloat(value)));
                this.item.complete = math.round(v, 2) + '%';
                this.recalcMinMax()
                this.$forceUpdate()
                this.$emit('changed');
            }
        },
        toggleOpened() {
            this.opened = !this.opened;
        },
        hasItemChanged() {
            return this.item.complete !== this.originalItemComplete
        },
        hasComponentChanged(component) {
            let o = this.originalComponentComplete.find((i) => i.id == component.id);
            return !o || (component.complete !== o.complete)
        },
        recalcMinMax() {
            let min = 0
            let tot = 0
            this.item.components.forEach(component => {
                min += ((parseFloat(component.complete) / 100) * component.ratio) * 100
                tot += (component.ratio * 100)
            })
            this.itemMaxPercent = (100 - (tot - min)) + '%'
            this.itemMinPercent = math.min(100, math.max(0, min)) + '%';
            let v = math.min(math.max(parseFloat(this.item.complete), parseFloat(this.itemMinPercent)), parseFloat(this.itemMaxPercent));
            if (v < 20) {
                this.item.complete = math.floor(v, 2) + '%';
            } else if (v > 80) {
                this.item.complete = math.ceil(v, 2) + '%';
            } else {
                this.item.complete = math.round(v, 2) + '%';
            }
        },
        showFS(fs) {
            this.fsModal.openModal(this.progress.filesystemId, fs);
        },

    },
    mounted() {
        this.recalcMinMax()
        this.canBeOpened = this.$refs.descLine.scrollHeight > this.$refs.descLine.getBoundingClientRect().height
    }
}
</script>
